import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import 'react-datepicker/dist/react-datepicker.css';
import { daysAgo, isSameDay, startOfMonth, today } from '../utils/datetime';

registerLocale('pt-BR', ptBR);

// Os atalhos falam em dias de São Paulo, não do relógio do navegador: "Hoje" é
// o dia da operação mesmo que a máquina esteja em outro fuso.
export const PERIOD_SHORTCUTS = [
  { label: 'Hoje', getStart: today },
  { label: '7 dias', getStart: () => daysAgo(7) },
  { label: '30 dias', getStart: () => daysAgo(30) },
  { label: 'Este mês', getStart: startOfMonth },
];

// As datas do calendário são dias (Date de meia-noite local carregando o dia
// escolhido), não instantes: formatá-las sem declarar fuso é o correto aqui.
const formatRange = (start, end) => {
  if (!start) return 'Selecione o período';

  const finish = end || start;
  const sameYear = start.getFullYear() === finish.getFullYear();

  const short = (date) =>
    date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const full = (date) => date.toLocaleDateString('pt-BR');

  if (isSameDay(start, finish)) return full(start);

  return sameYear ? `${short(start)} – ${full(finish)}` : `${full(start)} – ${full(finish)}`;
};

/** Botão que abre o calendário, no mesmo desenho dos demais filtros da barra. */
const PeriodInput = forwardRef(({ startDate, endDate, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    className="flex w-[15rem] items-center gap-2 rounded-md border border-gray-300 bg-white py-2 pl-3 pr-3 text-left text-sm transition-colors hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <CalendarDaysIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
    <span className={`flex-1 truncate ${startDate ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
      {formatRange(startDate, endDate)}
    </span>
  </button>
));

PeriodInput.displayName = 'PeriodInput';

/**
 * Seletor de período: um campo único que abre dois meses lado a lado com
 * atalhos rápidos no topo do popover.
 *
 * Enquanto o intervalo está pela metade (só a data inicial escolhida) o
 * componente não propaga a mudança — evita uma busca intermediária com o
 * período aberto até hoje.
 */
const PeriodPicker = ({ startDate, endDate, onChange }) => {
  const pickerRef = useRef(null);

  const activeShortcut = useMemo(() => {
    if (!isSameDay(endDate, today())) return null;

    return (
      PERIOD_SHORTCUTS.find((shortcut) => isSameDay(startDate, shortcut.getStart()))?.label ?? null
    );
  }, [startDate, endDate]);

  const applyShortcut = useCallback(
    (shortcut) => {
      onChange(shortcut.getStart(), today());
      pickerRef.current?.setOpen(false);
    },
    [onChange]
  );

  const CalendarWithShortcuts = useCallback(
    ({ className, children }) => (
      <div className={`${className} period-picker-calendar !border-gray-200 shadow-lg`}>
        <div className="flex flex-wrap gap-1 border-b border-gray-100 bg-white p-2">
          {PERIOD_SHORTCUTS.map((shortcut) => (
            <button
              key={shortcut.label}
              type="button"
              onClick={() => applyShortcut(shortcut)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                activeShortcut === shortcut.label
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {shortcut.label}
            </button>
          ))}
        </div>
        <div className="bg-white">{children}</div>
      </div>
    ),
    [activeShortcut, applyShortcut]
  );

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">Período</label>
      <DatePicker
        ref={pickerRef}
        locale="pt-BR"
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={([start, end]) => onChange(start, end)}
        onCalendarClose={() => {
          // Intervalo deixado pela metade vira um único dia
          if (startDate && !endDate) onChange(startDate, startDate);
        }}
        monthsShown={2}
        maxDate={today()}
        dateFormat="dd/MM/yyyy"
        showPopperArrow={false}
        popperPlacement="bottom-start"
        calendarContainer={CalendarWithShortcuts}
        customInput={<PeriodInput startDate={startDate} endDate={endDate} />}
      />
    </div>
  );
};

export default PeriodPicker;
